import React, { useContext, useLayoutEffect } from 'react';
import { t } from 'i18next';
import Button from '@components-clean/core/button';
import { CommentsContext } from '@components-clean/core/comments-side-bar/comments-context';
import CommentForm, { CommentFormInput } from '@components-clean/core/comments-side-bar/components/comment-form';
import styles from '@components-clean/core/comments-side-bar/styles.module.scss';
import { TextAreaController } from '@components-clean/core/text-area';
import { ReadOnlyContext } from '@contexts-clean/read-only-context';

interface CommentFormComponentProps {
    defaultContent?: string;
    onDirtyStateChanged: (isDirty: boolean) => void;
    isUpdate?: boolean;
    isReply?: boolean;
    onSave: (content: string) => Promise<boolean>;
    onClose: () => void;
    disableSave: boolean;
}

const CommentFormComponent = ({
    defaultContent,
    onDirtyStateChanged,
    isUpdate,
    isReply,
    onSave,
    onClose,
    disableSave,
}: CommentFormComponentProps) => {
    const { readOnly } = useContext(ReadOnlyContext);
    const { confirmDiscardChanges } = useContext(CommentsContext);

    const { control, handleSubmit, isValid, isDirty, isSubmitting } = CommentForm(defaultContent ?? '');

    const onSubmit = async (commentFormInput: CommentFormInput) => {
        onDirtyStateChanged(false);
        if (await onSave(commentFormInput.content)) {
            onClose();
            return;
        }
        onDirtyStateChanged(isDirty);
    };

    const onCancel = async () => {
        if (!isDirty || (await confirmDiscardChanges!())) {
            onClose();
        }
    };

    useLayoutEffect(() => {
        onDirtyStateChanged(isDirty);
    }, [isDirty]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    marginBottom: '6px',
                }}
            >
                <Button data-testid="cancel-button" onClick={onCancel}>
                    {t('common:cancel')}
                </Button>
                <Button
                    data-testid="save-button"
                    disabled={!isValid || !isDirty || isSubmitting || disableSave}
                    primary
                    onClick={() => handleSubmit(onSubmit)()}
                >
                    {t(isUpdate ? 'common:update' : 'common:save')}
                </Button>
            </div>
            <TextAreaController
                disabled={readOnly}
                control={control}
                placeholder={isReply ? t('comments:addAReply') : t('comments:addAComment')}
                className={styles.commentTextArea}
                name="content"
                data-testid="edit-textbox"
                required
                maxLength={300}
                validationTooltipPosition="left"
            />
        </div>
    );
};

export default CommentFormComponent;
